import {
  assert,
  Ref,
  RefMap,
  RefSet,
  notNull,
} from 'src/util';

import StoreImport from './Store';

import Calculator from './Calculator';
import Room from './Room';
import RoomPage from './RoomPage';

type App = {
  location: App.Location,
  entities: {
    rooms: RefSet<Room>,
  },
  extraRoomHints: { [id: string]: string | null },
  roomPages: RefMap<Room, RoomPage>,
  calculator: Calculator.SA['state'],
};

function App(initRoomId: string | null, roomHints?: { [id: string]: string | null }): App {
  // TODO: Default to generated name instead of handling null names?
  const room = Room({ id: initRoomId });
  const calculator = Calculator();
  const page = RoomPage(room.ref);

  return {
    location: { type: 'r', data: room.ref },
    entities: {
      rooms: RefSet.update(RefSet<Room>(), room),
    },
    extraRoomHints: roomHints || {},
    roomPages: RefMap.set(RefMap<Room, RoomPage>(), room.ref, page),
    calculator,
  };
}

namespace App {
  export type Location = (
    { type: 'r', data: Ref<Room> } |
    never
  );

  export function Location(app: App, str: string): Location | null {
    const parts: (string | undefined)[] = str.split('/');
    const [prefix, type, ...data] = parts;

    if (prefix !== '') {
      return null;
    }

    switch (type) {
      case 'r': {
        // /r/<id>

        if (data.length !== 1) {
          return null;
        }

        const [id] = data;

        if (id === undefined) {
          return null;
        }

        return { type: 'r', data: Ref.fromId<Room>(id) };
      }
    }

    return null;
  }

  export namespace Location {
    export function toString(location: Location): string {
      return `/${location.type}/${location.data.id}`;
    }
  }

  export type Page = (
    RoomPage |
    never
  );

  export function CurrentPage(app: App): App.Page | null {
    switch (app.location.type) {
      case 'r': {
        return RefMap.get(app.roomPages, app.location.data);
      }
    }
  }

  export type Action = (
    {
      type: 'AddRoom',
      data: Room,
    } |
    {
      type: 'SetRoom',
      data: Room,
    } |
    {
      type: 'SetRoomIdName',
      data: {
        id: string,
        name: string | null,
      },
    } |
    {
      type: 'SetRoomHints',
      data: { [id: string]: string | null },
    } |
    {
      type: 'Room',
      roomRef: Ref<Room>,
      subAction: Room.Action,
    } |
    {
      type: 'RoomPage',
      room: Room,
      subAction: RoomPage.Action,
    } |
    {
      type: 'Calculator',
      subAction: Calculator.SA['action'],
    } |
    {
      type: 'multi',
      subActions: Action[],
    } |
    never
  );

  function reducePass(app: App, action: Action): App {
    switch (action.type) {
      case 'AddRoom': {
        return { ...app,
          entities: { ...app.entities,
            rooms: RefSet.update(
              app.entities.rooms,
              action.data,
            ),
          },
        };
      }

      case 'SetRoom': {
        let page = RefMap.get(app.roomPages, action.data.ref);

        if (page === null) {
          page = RoomPage(action.data.ref);

          app = { ...app,
            roomPages: RefMap.set(
              app.roomPages,
              action.data.ref,
              page,
            ),
          };
        }

        app = { ...app,
          location: {
            type: 'r',
            data: action.data.ref
          },
        };

        return app;
      }

      case 'SetRoomIdName': {
        let room = RefSet.resolve(app.entities.rooms, Ref.fromId<Room>(action.data.id));

        if (room === null) {
          room = Room(action.data);
        } else if (room.name !== action.data.name) {
          room = { ...room,
            name: action.data.name,
          };
        }

        app = { ...app,
          entities: { ...app.entities,
            rooms: RefSet.update(app.entities.rooms, room),
          },
        };

        return reduce(app, {
          type: 'SetRoom',
          data: room,
        });
      }

      case 'SetRoomHints': {
        return { ...app,
          extraRoomHints: action.data,
        };
      }

      case 'Room': {
        const room = RefSet.resolve(app.entities.rooms, action.roomRef);

        if (room === null) {
          // TODO: logging?
          return app;
        }

        return { ...app,
          entities: { ...app.entities,
            rooms: RefSet.update(
              app.entities.rooms,
              Room.reduce(room, action.subAction)
            ),
          },
        };
      }

      case 'RoomPage': {
        let page = (
          RefMap.get(app.roomPages, action.room.ref) ||
          RoomPage(action.room.ref)
        );

        page = RoomPage.reduce(page, app, action.subAction);

        return { ...app,
          roomPages: RefMap.set(
            app.roomPages,
            action.room.ref,
            page,
          ),
        };
      }

      case 'Calculator': {
        return { ...app,
          calculator: Calculator.reduce(app.calculator, action.subAction),
        };
      }

      case 'multi': {
        return action.subActions.reduce(reducePass, app);
      }
    }
  }

  export function reduce(app: App, action: Action): App {
    app = reducePass(app, action);
    return app;
  }

  export function Rooms(app: App) {
    return (RefSet
      .Refs(app.entities.rooms)
      .map(ref => RefSet.resolve(app.entities.rooms, ref))
      .filter(notNull)
    );
  }

  export function RoomHints(app: App): { [id: string]: string | null } {
    let res = app.extraRoomHints;

    for (const room of Rooms(app)) {
      res = { ...res,
        [room.ref.id]: room.name,
      };
    }

    return res;
  }

  export function CurrentRoom(app: App): Room | null {
    if (app.location.type === 'r') {
      return RefSet.resolve(
        app.entities.rooms,
        app.location.data,
      );
    }

    return null;
  }

  export function PreviousRoom(app: App, roomRef: Ref<Room>): Room | null {
    const room = RefSet.resolve(app.entities.rooms, roomRef);

    if (!room) {
      return null;
    }

    const gameList = room.gameList.slice();

    if (gameList.length === 0) {
      return null;
    }

    gameList.pop();
    return { ...room, gameList };
  }

  export function needsTimeUpdate(app: App): boolean {
    const page = CurrentPage(app);

    if (page === null) {
      return false;
    }

    return RoomPage.needsTimeUpdate(page, app);
  }

  export function scaleRating(r: number) {
    return 1500 + 200 / Math.log(3) * r;
  }

  export function ratingPieSize(unscaledRating: number) {
    return Math.exp(unscaledRating);
  }

  export function winChance(player1Rating: number, player2Rating: number) {
    const pie1 = ratingPieSize(player1Rating);
    const pie2 = ratingPieSize(player2Rating);

    return pie1 / (pie1 + pie2);
  }

  export type X = { state: App, action: Action };
  export type Store = ReturnType<typeof Store>;

  export function Store(initRoomId: string | null = null, roomHints?: { [id: string]: string | null }) {
    const baseStore = StoreImport<X>(App(initRoomId, roomHints), App.reduce);

    return { ...baseStore,
      dispatchRoom: (
        roomRef: Ref<Room>,
        subAction: Room.Action
      ) => baseStore.dispatch({
        type: 'Room',
        roomRef,
        subAction,
      }),
      dispatchCurrentRoomPage: (subAction: RoomPage.Action) => {
        const app = baseStore.getState();
        const room = App.CurrentRoom(app);

        if (room === null) {
          assert(false);
          return;
        }

        baseStore.dispatch({
          type: 'RoomPage',
          room,
          subAction,
        });
      },
      dispatchRoomPage: (roomRef: Ref<Room>, subAction: RoomPage.Action) => {
        const app = baseStore.getState();

        const room = RefSet.resolve(app.entities.rooms, roomRef);

        if (room === null) {
          console.warn('Expected room ref to resolve');
          return;
        }

        baseStore.dispatch({
          type: 'RoomPage',
          room,
          subAction,
        });
      },
    };
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/state/App.ts